import { useState, useEffect } from "react"
import { css } from "@emotion/core"
import { string, array, number } from "prop-types"
import { pluralize } from "utils/helpers"
import { api } from "utils/api"
import Row from "./row"
import NewScholarship from "./new_scholarship"
import { colors } from "components/shared/admin_colors"
import { Overflowing } from "components/ui"
import { StackView } from "@planningcenter/tapestry-react"

const Index = props => {
  const [scholarships, setScholarships] = useState(props.scholarships)
  const [editable, setEditable] = useState({ id: null, isEditing: null })
  const { blankStateImageURL } = props

  useEffect(() => {
    const handler = e => {
      if (editable.isEditing && e.keyCode === 27) {
        return handleCancel()
      }
    }

    window.addEventListener("keyup", handler)
    return () => window.removeEventListener("keyup", handler)
  })

  const handleEdit = id => {
    setEditable({
      id,
      isEditing: true
    })
  }

  const handleCancel = () => {
    setEditable({
      id: null,
      isEditing: false
    })
  }

  const handleUpdate = params => {
    const { name, total_amount } = params

    api
      .put(`/scholarships/${params.id}`, {
        scholarship: { name, total_amount }
      })
      .then(resp => {
        // Update Scholarship by ids

        setScholarships(scholarships.map(s => (s.id === resp.id ? { ...s, ...resp } : s)))
        // Reset Editable Rows
        handleCancel()
      })
  }

  const handleDelete = params => {
    alertModal(
      {
        title: "Delete Scholarship?",
        text: "This can't be undone.",
        type: "warning",
        showCancelButton: true,
        cancelButtonText: "No, don't delete it",
        confirmButtonText: "Yes, delete it!",
        customClass: "destructive-warning",
        closeOnConfirm: true
      },
      () => {
        api.del(`/scholarships/${params.id}`).then(resp => {
          // Remove Scholarshpi from Ids
          setScholarships(scholarships.filter(s => s.id !== resp.id))
          // Reset Editable Rows
          handleCancel()
        })
      }
    )
  }

  const handleCreate = params => {
    api.post(`/signups/${props.eventId}/scholarships`, { scholarship: params }).then(resp => {
      // Update Scholarship State with new scholarship
      setScholarships(scholarships.concat(resp))
      // Reset Editable Rows
      handleCancel()
    })
  }

  const newRowStyle = css`
    td {
      background-color: ${colors.neutral_100};
      padding-top: 8px;
      padding-bottom: 8px;
      display: table-cell;
    }
  `

  return (
    <>
      {scholarships.length > 0 ? (
        <div className="pane">
          <div className="pane__header d-f@sm ai-c jc-sb">
            <h1 className="h2 mb-0">
              {pluralize("scholarship", "scholarships", scholarships.length)}
            </h1>
            <div className="mt-1 mt-0@sm">
              <button className="create-btn btn2" onClick={() => handleEdit("new")}>
                New scholarship
              </button>
            </div>
          </div>
          <Overflowing className="Overflowing">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Amount</th>
                  <th>Amount Used</th>
                  <th>Amount Remaining</th>
                </tr>
              </thead>
              <tbody>
                {editable.isEditing && editable.id === "new" && (
                  <tr css={newRowStyle}>
                    <NewScholarship
                      scholarship={{ id: "new" }}
                      onCancel={handleCancel}
                      onSave={handleCreate}
                      fieldWrapper={"td"}
                    />
                  </tr>
                )}
                {scholarships.length > 0 && (
                  <>
                    {scholarships.map(s => (
                      <Row
                        key={s.id}
                        editable={editable}
                        onEdit={handleEdit}
                        onCancel={handleCancel}
                        scholarship={s}
                        onSave={handleUpdate}
                        onDelete={handleDelete}
                      />
                    ))}
                  </>
                )}
              </tbody>
            </table>
          </Overflowing>
        </div>
      ) : (
        <>
          <div className="blank-state">
            <div className="blank-state__hero ta-c">
              <img src={blankStateImageURL} alt="No results" />
            </div>
            <div className="blank-state__msg blank-state__msg--wide">
              <div className="mb-2">
                <div>
                  With scholarships, you can designate available funds and apply them toward
                  registrations.
                </div>
                <a href="https://pcoregistrations.zendesk.com/hc/en-us/articles/4428041732891">
                  Learn more about scholarships
                </a>{" "}
                or create one below.
              </div>
            </div>
          </div>
          <div className="blank-state__msg blank-state__msg--wide">
            <StackView
              marginBottom={1}
              alignment="center"
              axis="vertical"
              mediaQueries={{
                sm: { alignment: "center", axis: "horizontal" }
              }}
            >
              <NewScholarship
                scholarship={{ id: "new" }}
                onSave={handleCreate}
                submitButtonLabel="Add scholarship"
                fieldWrapper="div"
              />
            </StackView>
          </div>
        </>
      )}
    </>
  )
}

Index.defaultProps = {
  blankStateImageURL: "/static/blank-states/registrations-forms.svg"
}

Index.propTypes = {
  scholarships: array.isRequired,
  eventId: number.isRequired,
  blankStateImageURL: string.isRequired
}

export default Index
